import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import Carousel, { slidesToShowPlugin, Dots } from '@brainhubeu/react-carousel';
import stylistStatus from 'constants/stylistStatus';
import { locationSelector } from 'redux/locations/selectors';
import { useSelector } from 'react-redux';
import entitySelectors from 'redux/entities/selectors';
import { breakpoints } from 'utils/rwd';
import * as Ui from './OurStylists.styles';
import { Stylist } from './components/Stylist';
import LocationPageModulePlaceholder from '../../EmptyModulePlaceholder/EmptyModulePaceholder';

const OurStylistsBanner = ({ component }) => {
  const { participants } = useSelector(locationSelector);
  const selectedSellersWithAccount = participants.filter(seller => {
    const isIncluded = component?.artists?.includes(seller?.connected_user?.uid);
    const isApproved = seller.profile_page_status === stylistStatus.APPROVED && !seller.is_archived;
    return isIncluded && isApproved;
  });

  const customArtists = useSelector(entitySelectors.custom_artistsSelector);

  const selectedCustomArtists = customArtists.filter(ca =>
    component.custom_artists?.includes(ca.uid),
  );
  const selectedSellers = selectedSellersWithAccount.concat(selectedCustomArtists);

  if (selectedSellers.length === 0) {
    return (
      <Row>
        <LocationPageModulePlaceholder />
      </Row>
    );
  }

  return <StylistPureComponent selectedSellers={selectedSellers} title="Artists" />;
};

const StylistPureComponent = ({ selectedSellers, title, widget }) => {
  const [activeSlide, setActiveSlide] = useState(1);

  const isSmallerThenLg = useMediaQuery({
    query: `(max-width: ${breakpoints.lg}px)`,
  });

  const showCarouselNavigation = isSmallerThenLg
    ? selectedSellers.length > 2
    : selectedSellers.length > 12;

  const onChange = useCallback(value => {
    setActiveSlide(value);
  }, []);

  const carouselPlugins = useMemo(() => {
    const plugins = [
      {
        resolve: slidesToShowPlugin,
        options: {
          numberOfSlides: isSmallerThenLg ? 2.3 : 6,
        },
      },
    ];

    return plugins;
  }, [isSmallerThenLg]);

  const renderStylists = useCallback(() => {
    if (showCarouselNavigation && !widget) {
      return (
        <Ui.CarouselWrapper widget={widget}>
          <Carousel value={activeSlide} onChange={onChange} plugins={carouselPlugins}>
            {selectedSellers.map(stylist => (
              <Stylist key={stylist.id} stylist={stylist} />
            ))}
          </Carousel>
        </Ui.CarouselWrapper>
      );
    }
    return (
      <Ui.StylistRow widget={widget}>
        <Ui.StyledScrollContainer vertical>
          {selectedSellers.map(stylist => (
            <Stylist noSlide key={stylist.id} stylist={stylist} />
          ))}
        </Ui.StyledScrollContainer>
      </Ui.StylistRow>
    );
  }, [isSmallerThenLg, carouselPlugins, activeSlide, selectedSellers]);

  return (
    <Ui.Wrapper widget={widget}>
      <Ui.Container>
        <Ui.SectionTitle>{title}</Ui.SectionTitle>
        {renderStylists()}
        {!!showCarouselNavigation && !widget && (
          <Ui.DotsWrapper>
            <Dots value={activeSlide} onChange={setActiveSlide} number={selectedSellers.length} />
          </Ui.DotsWrapper>
        )}
      </Ui.Container>
    </Ui.Wrapper>
  );
};

OurStylistsBanner.propTypes = {
  component: PropTypes.object.isRequired,
};

StylistPureComponent.propTypes = {
  selectedSellers: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  widget: PropTypes.bool,
};

export default OurStylistsBanner;
export { StylistPureComponent };
